import { useState, useCallback, useMemo } from "react";
import { Calendar } from "lucide-react";
import { Button } from "reactstrap";

import { GroupPaymentModal } from "./GroupPaymentModal";
import { formatGridCurrency } from "../utils/formatters";

interface GroupHeaderWithModalProps {
  data: any;
  groupType: "dateGroup";
  milestones: any[];
}

/**
 * Date Group Header - Top level grouping by date
 * Handles date bucket formats: "YYYY-MM-01", "YYYY-MM-15", or "unknown"
 */
export const DateGroupHeaderRenderer = ({
  data,
  milestones,
}: GroupHeaderWithModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Format the date display based on the bucket type - memoized for performance
  const formattedDate = useMemo(() => {
    const dateStr = data.date;
    if (dateStr === "unknown") {
      return "Unknown Date";
    }

    // Handle YYYY-MM-01 and YYYY-MM-15 formats
    if (dateStr.endsWith("-01")) {
      const [year, month] = dateStr.split("-");
      return `${year}-${month} (1st-14th)`;
    } else if (dateStr.endsWith("-15")) {
      const [year, month] = dateStr.split("-");
      return `${year}-${month} (15th-End)`;
    }

    return dateStr; // fallback
  }, [data.date]);

  const handlePayAll = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <>
      <div className="accounting-group-header accounting-group-header--date d-flex align-items-center justify-content-between px-3 py-2">
        <div className="d-flex align-items-center gap-2">
          <Calendar size={18} />
          <strong>{formattedDate}</strong>
        </div>
        <div className="d-flex align-items-center gap-3">
          <span className="fw-bold">
            {formatGridCurrency(data.approvedAmount)}
          </span>
          <Button color="dark" size="sm" outline onClick={handlePayAll}>
            Pay All in Group ({data.approvedCount})
          </Button>
        </div>
      </div>

      <GroupPaymentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        milestones={milestones}
        groupType="dateGroup"
        groupName={formattedDate}
      />
    </>
  );
};

/**
 * Factory function to create the appropriate group header renderer
 * Only handles date group headers since we simplified to single-level grouping
 */
export const createGroupHeaderRenderer = () => {
  return (params: any) => {
    const { data } = params;

    if (!data) {
      return <div>No data</div>;
    }

    // Use the items array directly from the group data (already provided by backend)
    const milestones = data.items || [];

    if (data.rowType === "dateGroup") {
      return (
        <DateGroupHeaderRenderer
          data={data}
          milestones={milestones}
          groupType="dateGroup"
        />
      );
    }

    return <div>Unknown group type: {data.rowType}</div>;
  };
};
